import moment from 'moment-timezone'
import { useContext } from 'react'
import { mapValues } from 'lodash'
import { TimeZoneContext } from '../../shared/context'
import { MDate } from '../../types/whats-on'
import { isTomorrow, isToday } from './date-helpers'
import { TODAY_STRING, TOMORROW_STRING, formatDay } from './date-format-helpers'

type DatetimeKeys = 'showAt' | 'hideAt' | 'startAt' | 'endAt' | 'openAt'

type HasDatetimeKeys = Partial<Record<DatetimeKeys, string>>

type DatetimeStringsOfEntry<T> = Pick<T, keyof T & DatetimeKeys>

type MDatesOfEntry<T> = Record<keyof DatetimeStringsOfEntry<T>, MDate>

export class CentreTime {
  private timeZone: string
  public constructor(timeZone: string) {
    this.timeZone = timeZone
  }

  private assertCentreTime(dt: MDate): boolean {
    if (this.timeZone !== dt.tz()) {
      throw new Error(
        'Trying to compare times from two different centre timezones'
      )
    }
    return true
  }

  public fromISOIgnoringOffset(isoString: string): MDate {
    const OFFSETLESS = 'YYYY-MM-DDTHH:mm:SS'
    const OFFSETFUL = 'YYYY-MM-DDTHH:mm:SSZZ'
    const parsedZone = moment.parseZone(isoString, OFFSETFUL, true)
    const ignoringOffset = parsedZone.isValid()
      ? parsedZone.format(OFFSETLESS)
      : isoString
    return moment.tz(ignoringOffset, this.timeZone)
  }

  public fromEntryISO<T extends HasDatetimeKeys>(entry: T): MDatesOfEntry<T> {
    const { showAt, hideAt, startAt, endAt, openAt } = entry
    return mapValues({ showAt, hideAt, startAt, endAt, openAt }, v =>
      v ? this.fromISOIgnoringOffset(v) : undefined
    ) as MDatesOfEntry<T>
    // I'd also like to throw in a mapKeys so the properties have a transformed name
    // e.g. centreShowAt, but not sure how to keep typescript happy.
  }

  public formatHumanDay(dt: MDate): string {
    this.assertCentreTime(dt)
    if (isToday(dt, this.timeZone)) {
      return TODAY_STRING
    }
    if (isTomorrow(dt, this.timeZone)) {
      return TOMORROW_STRING
    }

    return formatDay(dt)
  }
}

export default function useCentreTime(): CentreTime | null {
  const timeZone = useContext(TimeZoneContext)
  if (!timeZone) {
    return null
  }
  return new CentreTime(timeZone)
}
